import React, { useEffect, useRef, useState } from 'react';
import { FiArchive, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import { useArchiveChat } from '../../features/chat/useArchiveChat';
import '../../styles/ArchiveChatButton.css';

interface ArchiveChatButtonProps {
    conversationId: string;
    title?: string;
}

/**
 * Archive action for a row in any chat-history list. Archiving is a one-way soft
 * delete, so the first click only asks "Archive?" inline; the second confirms.
 * Every click/keypress is kept from reaching the row (which would open the chat).
 * The row itself disappears once archived — lists listen for CHAT_ARCHIVED_EVENT.
 */
const ArchiveChatButton: React.FC<ArchiveChatButtonProps> = ({ conversationId, title }) => {
    const archiveChat = useArchiveChat();
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const wrapRef = useRef<HTMLSpanElement>(null);

    // Clicking anywhere else, or Escape, cancels the confirm.
    useEffect(() => {
        if (!confirming) return;
        const onDown = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setConfirming(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setConfirming(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [confirming]);

    const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };

    const confirm = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setBusy(true);
        setError('');
        try {
            await archiveChat(conversationId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not archive this chat.');
            setConfirming(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <span
            ref={wrapRef}
            className={`archive-chat${confirming || busy ? ' archive-chat--open' : ''}${error ? ' archive-chat--error' : ''}`}
            onClick={stop}
            onMouseDown={stop}
            onKeyDown={stop}
        >
            {busy ? (
                <span className="archive-chat__busy" aria-live="polite" aria-label="Archiving">
                    <FiRefreshCw size={12} className="archive-chat__spin" />
                </span>
            ) : confirming ? (
                <span className="archive-chat__confirm" role="group" aria-label="Archive this chat?">
                    <button type="button" className="archive-chat__yes" onClick={confirm} title="Archive this chat">
                        <FiCheck size={12} /> Archive
                    </button>
                    <button type="button" className="archive-chat__no" onClick={(e) => { e.stopPropagation(); setConfirming(false); }} title="Cancel" aria-label="Cancel">
                        <FiX size={12} />
                    </button>
                </span>
            ) : (
                <button
                    type="button"
                    className="archive-chat__btn"
                    onClick={(e) => { e.stopPropagation(); setError(''); setConfirming(true); }}
                    title={error || title || 'Archive chat'}
                    aria-label={title || 'Archive chat'}
                >
                    <FiArchive size={12} />
                </button>
            )}
        </span>
    );
};

export default ArchiveChatButton;
